import trim from "lodash/trim";
import { MatchingRelationType } from "PFApp/constants/matching_relation_type";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import api from "PFCore/api";
import { RequestConfig } from "PFCore/api/api";
import { getProfileName } from "PFCore/helpers/profile";
import { HighlightsParam } from "PFCore/types/highlights_param";
import {
  BasicProfile,
  Collection,
  CurrentProfile,
  Id,
  MatchedActivity,
  MatchedProfile,
  PaginatedCollection,
  Profile
} from "PFTypes";

import { QueryParams } from "../types/query_params";

export const fetchProfile = (profileId: Id): Promise<Profile> =>
  api<Profile>({
    url: `profiles/${profileId}`
  });

export const fetchSuggestedProfile = (profileId: Id): Promise<CurrentProfile> =>
  api<CurrentProfile>({
    url: `profiles/${profileId}/suggested`
  });

/**
 * - `"active"`: returns only active profiles
 * - `"visible"`: returns active profiles + suspended within retention period
 * - `"with_suspended"`: returns active profiles + all suspended regardless of retention period
 */
export type ProfilesOptionsProfileVisibility = "active" | "visible" | "with_suspended";

export type ProfilesOptionsParams = {
  term?: string;
  policyName?: string;
  value?: Id[];
  perPage?: number;
  profileVisibility?: ProfilesOptionsProfileVisibility;
};

export const fetchProfilesOptions = async ({
  term,
  policyName,
  value,
  perPage,
  profileVisibility = "visible"
}: ProfilesOptionsParams): Promise<Collection<(BasicProfile & { text: string })[]>> => {
  const trimmedTerm = trim(term);

  const response = await api<Collection<BasicProfile[]>>({
    url: "options/profiles",
    params: {
      ...(trimmedTerm ? { term: trimmedTerm } : {}),

      ...(policyName ? { policyName } : {}),
      ...(value ? { value, perPage: PER_PAGE_UNPAGINATED } : {}),
      ...(profileVisibility ? { profileVisibility } : {}),
      ...(perPage ? { perPage } : {})
    }
  });

  return {
    ...response,
    entries: response.entries.map((option) => ({
      ...option,
      text: getProfileName(option)
    }))
  };
};

export const suspendProfile = (profileId: number) =>
  api({ method: "PUT", url: `profiles/${profileId}/suspend` });

export type FetchProfileQueryParams = QueryParams &
  Partial<{
    full: boolean;
    nested: boolean;
    order_target?: "capacity_index";
    id: Id[];
  }> &
  HighlightsParam;

export type FetchProfilesResponse = PaginatedCollection<MatchedProfile[]>;

export const fetchProfiles = (
  data: FetchProfileQueryParams,
  config?: RequestConfig
): Promise<FetchProfilesResponse> =>
  api<FetchProfilesResponse>({
    url: "profiles/index",
    method: "POST",
    data,
    ...(config || {})
  });

export type FetchProfileMatchingRolesParams = {
  profileId: Id;
  relationType?: MatchingRelationType;
} & QueryParams;

export type FetchProfileMatchingRolesResponse = PaginatedCollection<MatchedActivity[]>;

export const fetchProfileMatchingRoles = ({ relationType, ...params }: FetchProfileMatchingRolesParams) =>
  api<FetchProfileMatchingRolesResponse>({
    url: "matching_activities",
    method: "post",
    data: {
      ...params,
      perPage: 10,
      relationType: relationType || MatchingRelationType.PrefilteredAndAvailable
    }
  });

export const deleteProfileAvatar = (profileId: Id) =>
  api<void>({
    url: `profiles/${profileId}/avatar`,
    method: "DELETE"
  });

export const deletePlaceholderProfiles = (profileIds: Id[]) =>
  api<void>({
    url: `profiles/bulk_destroy_placeholders`,
    method: "DELETE",
    data: {
      profileIds
    }
  });
